/**
 * Lazily loads the Razorpay Checkout script (checkout.js) on demand.
 *
 * It is intentionally NOT added to the global <head> (see app/layout.tsx) so it
 * doesn't drop ~30 third-party cookies on every page load. We inject it the
 * first time a user starts checkout and cache the load promise for reuse.
 */

const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let loadPromise: Promise<boolean> | null = null;

export interface RazorpayOptions {
    key?: string;
    order_id: string;
    amount: number | string;
    currency: string;
    name: string;
    description?: string;
    image?: string;
    prefill?: { name?: string; email?: string; contact?: string };
    notes?: Record<string, string>;
    theme?: { color?: string };
    handler?: (response: RazorpaySuccessResponse) => void;
    modal?: { ondismiss?: () => void };
}

export interface RazorpaySuccessResponse {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
}

export interface RazorpayInstance {
    open: () => void;
    on: (event: string, handler: (response: unknown) => void) => void;
}

declare global {
    interface Window {
        Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
    }
}

export function loadRazorpay(): Promise<boolean> {
    if (typeof window === "undefined") return Promise.resolve(false);
    if (window.Razorpay) return Promise.resolve(true);
    if (loadPromise) return loadPromise;

    loadPromise = new Promise<boolean>((resolve) => {
        const finish = (ok: boolean) => {
            if (!ok) loadPromise = null; // allow a retry on the next click
            resolve(ok);
        };

        const existing = document.querySelector<HTMLScriptElement>(
            `script[src="${RAZORPAY_SRC}"]`
        );
        if (existing) {
            if (window.Razorpay) return finish(true);
            existing.addEventListener("load", () => finish(true));
            existing.addEventListener("error", () => finish(false));
            return;
        }

        const script = document.createElement("script");
        script.src = RAZORPAY_SRC;
        script.async = true;
        script.onload = () => finish(true);
        script.onerror = () => finish(false);
        document.body.appendChild(script);
    });

    return loadPromise;
}
